import React, { useRef, useState } from 'react';
import { cn } from '@/lib/utils';

const KEY_STEP = 16;

interface Props {
  label: string;
  value: number;
  min: number;
  // The largest width allowed right now (the page keeps its minimum).
  getMax: () => number;
  onPreview: (value: number) => void;
  onCommit: (value: number) => void;
  // Which way the column grows: 'left' for a panel docked on the right, whose
  // handle is its left edge.
  growsTo?: 'left' | 'right';
}

// The line between two columns. Drag it, or focus it and use the arrow keys
// (Home / End for the limits). A window splitter in ARIA terms.
const ColumnResizeHandle: React.FC<Props> = ({ label, value, min, getMax, onPreview, onCommit, growsTo = 'right' }) => {
  const sign = growsTo === 'left' ? -1 : 1;
  const start = useRef<{ x: number; value: number; max: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const clamp = (v: number, max: number) => Math.round(Math.min(max, Math.max(min, v)));

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    start.current = { x: e.clientX, value, max: getMax() };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
    document.body.style.userSelect = 'none';
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!start.current) return;
    onPreview(clamp(start.current.value + sign * (e.clientX - start.current.x), start.current.max));
  };
  const end = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!start.current) return;
    const next = clamp(start.current.value + sign * (e.clientX - start.current.x), start.current.max);
    start.current = null;
    setDragging(false);
    document.body.style.userSelect = '';
    onCommit(next);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const max = getMax();
    const next =
      e.key === 'ArrowLeft'
        ? value - sign * KEY_STEP
        : e.key === 'ArrowRight'
          ? value + sign * KEY_STEP
          : e.key === 'Home'
            ? min
            : e.key === 'End'
              ? max
              : null;
    if (next == null) return;
    e.preventDefault();
    onCommit(clamp(next, max));
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={getMax()}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onKeyDown={onKeyDown}
      title="Arrastra para cambiar el ancho"
      className={cn(
        'group relative z-10 -mx-1 w-2 shrink-0 cursor-col-resize touch-none outline-none',
        'before:absolute before:inset-y-0 before:left-[3px] before:w-0.5 before:transition-colors',
        dragging ? 'before:bg-brand' : 'hover:before:bg-brand focus-visible:before:bg-brand',
      )}
    />
  );
};

export default ColumnResizeHandle;
